/**
 * @file LabelPrintResult.tsx
 * @description Результат печати в том же диалоге: «Готово: N листов, M
 *   этикеток», [Открыть для печати] [Скачать] (phase19.22, ui-design §1).
 *
 * PDF НЕ открывается автоматически: Safari/iOS блокирует `window.open` после
 * асинхронного запроса, поэтому открытие — отдельной кнопкой, по клику.
 * «Скачать» — обычная ссылка с `download` (тоже синхронно по клику): если
 * хранилище отдаёт файл с другого домена, браузер откроет его во вкладке, а
 * сохранить можно оттуда.
 */

import { CheckCircle2, Download, ExternalLink, TriangleAlert } from "lucide-react";

import { LABEL_TEXT, resultTitle } from "./label-texts";
import { Button } from "../../ui/button";
import type { LabelPrintFile, LabelPrintResult as Result } from "../../../lib/api-labels";

interface Props {
  result: Result;
  onMore: () => void;
  onClose: () => void;
}

/** Открыть PDF в новой вкладке — только из обработчика клика. */
function openFile(file: LabelPrintFile) {
  window.open(file.url, "_blank", "noopener");
}

export function LabelPrintResult({ result, onMore, onClose }: Props) {
  const file = result.files[0];
  return (
    <div className="flex flex-col gap-4">
      <p className="flex items-center gap-2 text-base font-medium">
        <CheckCircle2 className="size-5 text-primary" aria-hidden />
        {resultTitle(result.sheetsCount, result.labelsCount)}
      </p>
      <ul className="flex flex-col gap-1 text-sm text-on-surface-variant">
        {result.perTemplate.map((p) => (
          <li key={p.template}>
            {p.title}: {p.labels} эт., листов {p.sheets}
            {p.nextStartAt > 1 ? ` — в следующий раз начните с ${p.nextStartAt}-й` : ""}
          </li>
        ))}
      </ul>
      {result.warnings.map((w) => (
        <p key={w.code} className="flex items-start gap-1.5 text-xs text-warning">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {w.message}
        </p>
      ))}
      <p className="text-xs text-on-surface-variant">{LABEL_TEXT.printerHint}</p>
      {file ? (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button className="min-h-14 flex-1 text-base" onClick={() => openFile(file)}>
            <ExternalLink className="size-5" />
            <span className="ml-2">{LABEL_TEXT.open}</span>
          </Button>
          <Button asChild variant="outline" className="min-h-14 flex-1">
            <a href={file.url} download={file.fileName} target="_blank" rel="noopener noreferrer">
              <Download className="size-5" />
              <span className="ml-2">{LABEL_TEXT.download}</span>
            </a>
          </Button>
        </div>
      ) : null}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button variant="ghost" className="min-h-14 flex-1" onClick={onMore}>
          {LABEL_TEXT.printMore}
        </Button>
        <Button variant="outline" className="min-h-14 flex-1" onClick={onClose}>
          {LABEL_TEXT.done}
        </Button>
      </div>
    </div>
  );
}
